import { useTranslation } from 'react-i18next';
import { ContactInfoRow } from './ContactInfoRow';
import { Toggle } from '@/components';
import { SelectDropdown } from '@/components/SelectDropdown';
import type { ContactDetails } from '../../api/contacts';
import { useUserRoles } from '../../hooks';
import type { UserRole } from '../../api/employees';

const inputClasses =
  'py-1.5 px-2 border block w-full bg-field border-border rounded-sm text-sm text-onfield placeholder:text-onfield/30 focus:z-10 focus:border-primary focus:ring-primary focus:outline-none disabled:opacity-50 disabled:pointer-events-none';

export interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  isMainContact: boolean;
  username: string;
  roleId?: string;
}

interface ContactInfoSectionProps {
  contact: ContactDetails;
  formData: ContactFormData;
  isEditMode: boolean;
  companyId: string;
  onFieldChange: <K extends keyof ContactFormData>(
    field: K,
    value: ContactFormData[K]
  ) => void;
  onMainContactChange: (checked: boolean) => void;
}

export function ContactInfoSection({
  contact,
  formData,
  isEditMode,
  companyId,
  onFieldChange,
}: ContactInfoSectionProps) {
  const { t } = useTranslation();

  const hasUserWithDifferentEmail =
    contact.user && contact.user.userEmail !== contact.email;
  const hasUserWithDifferentPhone =
    contact.user && contact.user.userPhone && contact.user.userPhone !== contact.phoneNumber;

  const { data: roles, isLoading: rolesLoading } = useUserRoles(companyId);

  // Translated role name from the shared roles cache, falling back to the contact's own role name.
  const roleDisplayName =
    roles?.find((role) => role.id === contact.role?.id)?.name ?? contact.role?.name ?? '-';

  return (
    <div className="divide-y divide-border text-onsurface">
      <div
        className={`
          grid grid-cols-1 gap-0 overflow-hidden transition-all duration-300 ease-in-out divide-border
          ${isEditMode ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}
        `}
      >
        <div className="grid grid-cols-2 gap-4 py-3 border-b border-border items-center">
          <label className="text-sm opacity-50">{t('contact.fields.firstName')}</label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => onFieldChange('firstName', e.target.value)}
            placeholder={t('contact.placeholders.firstName')}
            className={inputClasses}
          />
        </div>
        <div className="grid grid-cols-2 gap-4 py-3 items-center">
          <label className="text-sm opacity-50">{t('contact.fields.lastName')}</label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => onFieldChange('lastName', e.target.value)}
            placeholder={t('contact.placeholders.lastName')}
            className={inputClasses}
          />
        </div>
      </div>

      <ContactInfoRow label={t('contact.fields.role')}>
        {isEditMode ? (
          <SelectDropdown
            value={formData.roleId || contact.role?.id || ''}
            onChange={(e) => onFieldChange('roleId', e.target.value)}
            disabled={rolesLoading}
          >
            <option value="">{t('employees.createForm.rolePlaceholder')}</option>
            {roles?.map((role: UserRole) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </SelectDropdown>
        ) : (
          <span>{roleDisplayName}</span>
        )}
      </ContactInfoRow>

      <ContactInfoRow label={t('contact.fields.email')}>
        {isEditMode ? (
          <input
            type="email"
            value={formData.email}
            onChange={(e) => onFieldChange('email', e.target.value)}
            placeholder={t('contact.placeholders.email')}
            className={inputClasses}
          />
        ) : (
          <div>
            <span>{contact.email}</span>
            {hasUserWithDifferentEmail && (
              <div className="text-xs opacity-50 mt-1">
                {t('contact.fields.userEmail')}: {contact.user?.userEmail}
              </div>
            )}
          </div>
        )}
      </ContactInfoRow>

      <ContactInfoRow label={t('contact.fields.phone')}>
        {isEditMode ? (
          <input
            type="tel"
            value={formData.phoneNumber}
            onChange={(e) => onFieldChange('phoneNumber', e.target.value)}
            placeholder={t('contact.placeholders.phone')}
            className={inputClasses}
          />
        ) : (
          <div>
            <span>{contact.phoneNumber || '-'}</span>
            {hasUserWithDifferentPhone && (
              <div className="text-xs opacity-50 mt-1">
                {t('contact.fields.userPhone')}: {contact.user?.userPhone}
              </div>
            )}
          </div>
        )}
      </ContactInfoRow>

      <ContactInfoRow label={t('contact.fields.mainContact')}>
        <Toggle
          checked={isEditMode ? formData.isMainContact : contact.isMainContact}
          onChange={isEditMode
            ? (checked) => onFieldChange('isMainContact', checked)
            : () => { }
          }
          ariaLabel={t('contact.fields.toggleMainContact')}
          size="sm"
          disabled={!isEditMode}
        />
      </ContactInfoRow>
      <ContactInfoRow label={t('contact.fields.user')}>
        <div className="flex flex-col gap-1">
          <span>{contact.user?.username || '-'}</span>
          <span>{contact.user?.userEmail || ''}</span>

        </div>

      </ContactInfoRow>

    </div>
  );
}
